// Customer-facing tracker step table and the pieces of the order state machine it reads.
//
// The authority for everything here is onward-hasura `docs/EVENT_TRANSITIONS.md`. Two rules
// from that doc drive the whole file:
//
//   1. `event_state` is the `next` of the most recent event, so a state was entered iff some
//      event carries `transition.next === <state>`.
//   2. "Data-only updates patch the event that entered the state owning the field" — a state's
//      timestamp lives on the event that entered it, never on a neighbouring event.
//
// Keep the maps below in step with that doc. `states_sorted` ordering is deliberately NOT used
// to decide completion: WAREHOUSE_RETURN and PENDING sort ahead of CD_RECEIVED because they are
// rewind-parking states rather than lifecycle positions, so an ordinal comparison against
// `event_state` un-completes the warehouse receipt when a *delivery* fails.

export const STEP_KEYS = {
    PLACED: 'PLACED',
    TEAM_ASSIGNED: 'TEAM_ASSIGNED',
    LTL_SCHEDULED: 'LTL_SCHEDULED',
    LTL_IN_TRANSIT: 'LTL_IN_TRANSIT',
    LTL_ARRIVED: 'LTL_ARRIVED',
    SCHEDULED: 'SCHEDULED',
    TIMEFRAME_CONFIRMED: 'TIMEFRAME_CONFIRMED',
    CD_IN_TRANSIT: 'CD_IN_TRANSIT',
    CD_ARRIVED: 'CD_ARRIVED',
    OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
    DELIVERY_ATTEMPTED: 'DELIVERY_ATTEMPTED',
    DELIVERY_COMPLETE: 'DELIVERY_COMPLETE',
    REVIEW_SUBMITTED: 'REVIEW_SUBMITTED',
};

export const STEP_LABELS = {
    [STEP_KEYS.PLACED]: 'Order Placed',
    [STEP_KEYS.TEAM_ASSIGNED]: 'Delivery Team Assigned',
    [STEP_KEYS.LTL_SCHEDULED]: 'LTL Pickup Scheduled',
    [STEP_KEYS.LTL_IN_TRANSIT]: 'LTL In Transit',
    [STEP_KEYS.LTL_ARRIVED]: 'Arrived at Local Warehouse',
    [STEP_KEYS.SCHEDULED]: 'Scheduled',
    [STEP_KEYS.TIMEFRAME_CONFIRMED]: 'Timeframe Confirmed',
    [STEP_KEYS.CD_IN_TRANSIT]: 'In Transit to Local Facility',
    [STEP_KEYS.CD_ARRIVED]: 'Arrived at Local Facility',
    [STEP_KEYS.OUT_FOR_DELIVERY]: 'Out for Delivery',
    [STEP_KEYS.DELIVERY_ATTEMPTED]: 'Delivery Attempted',
    [STEP_KEYS.DELIVERY_COMPLETE]: 'Delivery Complete',
    [STEP_KEYS.REVIEW_SUBMITTED]: 'Review Submitted',
};

// The date field a state's entering event carries, most specific first. Falls back to the
// event's `created_at` when none is set — attempt events are inserted as a bare
// `{ action, order_id }` by the exception flow, so `created_at` is the only stamp they have.
export const STATE_DATE_FIELDS = {
    AWAITING: ['received_date', 'est_ship_date'],
    MM_CD_PENDING: ['mm_est_ship_date', 'mm_scheduled_ship_date'],
    MM_CD_IN_TRANSIT: ['mm_ship_date'],
    MM_CD_RECEIVED: ['mm_received_date'],
    MM_CD_DELIVERY_PENDING: ['scheduled_ship_date'],
    MM_CD_OUT_FOR_DELIVERY: ['ship_date'],
    MM_CD_ARRIVED: ['arrived_date'],
    RECEIVING: ['received_date'],
    PICKED_UP: ['ship_date', 'est_pickup_date'],
    CD_PENDING_PO: ['ship_date'],
    CD_PENDING: ['ship_date', 'received_date'],
    CD_RECEIVED: ['received_date'],
    CD_COMPLETE: ['pickup_date'],
    WAREHOUSE_RETURN: ['received_date'],
    RETURN_TO_SENDER: ['received_date'],
    END: ['delivery_date'],
};

// Rewind edges, and the states whose step each one invalidates. A rewind moves `event_state`
// backwards, so without this the tracker keeps showing a step the freight has fallen back out
// of. Only the steps the rewind actually undoes are cleared — a failed delivery
// (CD_COMPLETE:ATTEMPT) sends the freight back to the warehouse but does not undo its arrival
// there, so CD_RECEIVED survives.
export const REWIND_CLEARS = {
    'CD_PENDING:ATTEMPT': ['CD_PENDING'],
    'CD_COMPLETE:ATTEMPT': ['CD_COMPLETE'],
    'CD_COMPLETE:CD_ATTEMPT': ['CD_COMPLETE'],
    'MM_CD_OUT_FOR_DELIVERY:MM_ATTEMPTED': ['MM_CD_ARRIVED'],
    'MM_CD_RECEIVED:ADD_MM_CD': ['MM_CD_IN_TRANSIT', 'MM_CD_RECEIVED'],
    'END:REVERT': ['END'],
    // Retired, but historical orders still carry it (EVENT_TRANSITIONS.md § Retired).
    'PICKED_UP:REMOVE': ['PICKED_UP'],
};

// A failed delivery, whichever leg it happened on.
export const DELIVERY_ATTEMPT_ACTIONS = ['CD_COMPLETE:ATTEMPT', 'CD_COMPLETE:CD_ATTEMPT'];

// The states that clear an open attempt: the order is moving again, or it is done.
export const ATTEMPT_RESOLVING_STATES = ['CD_COMPLETE', 'END'];

// Grouped exactly as MM_STATE_MILESTONES in @onward-delivery/core groups them, so the customer
// page and the internal milestone read the same three positions off the same states. The two
// hub hops repeat the labels because the freight makes the same move twice.
export const LTL_STEP_STATES = {
    [STEP_KEYS.LTL_SCHEDULED]: ['MM_CD_PENDING', 'MM_CD_DELIVERY_PENDING'],
    [STEP_KEYS.LTL_IN_TRANSIT]: ['MM_CD_IN_TRANSIT', 'MM_CD_OUT_FOR_DELIVERY'],
    // RECEIVING is the terminal on-hand signal: MM_CD_ARRIVED:CONFIRMED lands there, and orders
    // whose middle mile is untracked reach it without ever visiting an MM_CD_* state.
    [STEP_KEYS.LTL_ARRIVED]: ['MM_CD_RECEIVED', 'MM_CD_ARRIVED', 'RECEIVING'],
};

export const ORDER_COMPLETE_STATUSES = ['complete', 'routedComplete'];
